import React, { useState } from 'react';
import { Box, Typography, Avatar, useTheme, Tooltip, Chip } from '@mui/material';
import { Business, VerifiedUser, Person, Phone, Email, Star, Circle } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from 'src/store';
import { selectConversationById } from 'src/features/account/conversations.slice';
import {  selectContactById, getContact } from 'src/features/account/contacts.slice';
import { ContactDrawer } from '../Contacts/ContactDrawer';
import ChannelEditDialog from './ChannelEditDialog';
import ContactCardV2 from '../Contacts/ContactCardV2';
import ContactEmailButton from '../Contacts/ContactEmailButton';

// Define props interface with optional parameters
interface ConversationContactsProps {
  conversation?: any;
  conversation_id?: string;
  hiddenRoles?: string[];
  sx?: any;
  onCloseConversation?: () => void;
}

// Component takes either conversation object or conversation_id (at least one must be provided)
const ConversationContacts: React.FC<ConversationContactsProps> = ({
  conversation,
  conversation_id,
  hiddenRoles = [],
  sx = {},
  onCloseConversation
}) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const dispatch = useAppDispatch();

  // State for dialog controls
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const assistantHidden = hiddenRoles.includes('assistant');
  const userHidden = hiddenRoles.includes('user') || hiddenRoles.includes('contact');

  // Only fetch from store if we don't have the conversation object already
  const conversationFromStore = useAppSelector(
    state => !conversation && conversation_id ? selectConversationById(state, conversation_id) : null
  );

  // Use provided conversation object or fallback to store-fetched one
  const activeConversation = conversation || conversationFromStore;

  // Check if we have full data or minimal data
  const hasFullData = activeConversation?._hasFullData !== false;

  // Handle both minimal and full payload formats for participating entities
  let userEntity = null;
  let assistantEntity = null;
  let participatingEntities = activeConversation?.participating_entities || [];

  if (hasFullData && Array.isArray(participatingEntities) && participatingEntities.length > 0) {
    // Full payload - entities are objects
    if (typeof participatingEntities[0] === 'object') {
      // Filter out function entities
      const filteredEntities = participatingEntities.filter(entity => entity.role !== 'function');
      userEntity = filteredEntities.find(entity => entity.role === 'user');
      assistantEntity = filteredEntities.find(entity => entity.role === 'assistant');
    }
  }

  const contactId = userEntity?.contact_id;

  // Try to get the contact from Redux store
  const contactFromStore = useAppSelector(state =>
    contactId ? selectContactById(state, contactId) : null
  );

  // Track loading contact IDs to avoid duplicate fetches
  const loadingContactsIds = useAppSelector(state => state.contacts.loadingContactsIds);

  // Local state to control tooltip open status (so we can trigger fetch on open)
  const [tooltipOpen, setTooltipOpen] = useState(false);
  
  const handleTooltipOpen = () => {
    // Fetch contact details lazily – only if not already in store or loading
    if (contactId && !contactFromStore && !loadingContactsIds.includes(contactId)) {
      dispatch(getContact(contactId));
    }
    setTooltipOpen(true);
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  // Root container styles
  const containerStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    ...sx,
  };

  // Card styles
  const cardStyles = {
    contactCard: {
      cursor: 'pointer',
      borderRadius: '6px',
      minWidth: '180px',
      maxWidth: '260px',
      minHeight: '90px',
      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
      padding: '10px',
      bgcolor: darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)',
      transition: 'all 0.2s ease-in-out',
      display: 'flex',
      flexDirection: 'row', // Changed to row to have avatar left, details right
      gap: '10px', // Space between avatar and details
      alignItems: 'flex-start', // Align items to the top
      '&:hover': {
        boxShadow: `0 2px 8px ${darkMode ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.1)'}`,
        borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
        transform: 'translateY(-1px)',
      },
    },
    minimalContact: {
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      p: 0,
      transition: 'all 0.2s ease',
      borderRadius: '4px',
      '&:hover': {
        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
      },
    },
    loadingCard: {
      cursor: 'pointer',
      borderRadius: '6px',
      minWidth: '180px',
      maxWidth: '260px',
      minHeight: '90px',
      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
      padding: '10px',
      bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
      display: 'flex',
      flexDirection: 'row', // Changed to row
      gap: '10px',
      alignItems: 'flex-start',
      opacity: 0.7,
    },
    assistantCard: {
      cursor: 'pointer',
      borderRadius: '6px',
      minWidth: '180px',
      maxWidth: '260px',
      minHeight: '90px',
      border: `1px solid ${darkMode ? 'rgba(100,100,255,0.2)' : 'rgba(0,50,200,0.1)'}`,
      padding: '10px',
      bgcolor: darkMode ? 'rgba(30,30,80,0.3)' : 'rgba(240,245,255,0.8)',
      transition: 'all 0.2s ease-in-out',
      display: 'flex',
      flexDirection: 'row', // Changed to row
      gap: '10px',
      alignItems: 'flex-start',
      '&:hover': {
        boxShadow: `0 2px 8px ${darkMode ? 'rgba(0,0,100,0.5)' : 'rgba(0,0,200,0.1)'}`,
        borderColor: darkMode ? 'rgba(100,100,255,0.3)' : 'rgba(0,50,200,0.2)',
        transform: 'translateY(-1px)',
      },
    },
    avatar: {
      width: 36,
      height: 36,
      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
      backgroundColor: darkMode ? theme.palette.grey[800] : theme.palette.grey[200],
      // Removed marginBottom as avatar is now in its own column
    },
    minimalAvatar: {
      width: 32,
      height: 32,
      fontSize: '0.875rem',
    },
    assistantAvatar: {
      width: 36,
      height: 36,
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
      // Removed marginBottom
    },
    detailsContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 0, // Important for text overflow to work properly
    },
    name: {
      fontWeight: 600,
      fontSize: '0.9rem',
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: darkMode ? theme.palette.grey[300] : theme.palette.grey[900],
      flex: 1,
    },
    detail: {
      fontSize: '0.7rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: darkMode ? theme.palette.grey[400] : theme.palette.grey[600],
      marginBottom: '2px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
  };

  // If no conversation data is available, show a placeholder
  if (!activeConversation) {
    return (
      <Box sx={containerStyles}>
        <Box sx={cardStyles.contactCard}>
          <Typography sx={cardStyles.name}>No contact data</Typography>
        </Box>
      </Box>
    );
  }

  // If we don't have full data yet, show basic contact info from participating entities
  if (!hasFullData) {
    const entities = activeConversation.participating_entities?.filter(e => 
      e.role !== 'assistant' && 
      e.role !== 'function' && 
      (e.name || e.email || e.phone)
    ) || [];
    
    if (entities.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.500' }}>
            <Person />
          </Avatar>
          <Typography variant="body2" color="text.secondary">No contact</Typography>
        </Box>
      );
    }
    
    const primaryEntity = entities[0];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32 }}>
          {primaryEntity.name?.[0] || primaryEntity.email?.[0] || '?'}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {primaryEntity.name || primaryEntity.email || primaryEntity.phone || 'Contact'}
          </Typography>
          {(primaryEntity.email || primaryEntity.phone) && (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {primaryEntity.email || primaryEntity.phone}
            </Typography>
          )}
        </Box>
        {entities.length > 1 && (
          <Chip size="small" label={`+${entities.length - 1}`} sx={{ height: 20 }} />
        )}
      </Box>
    );
  }

  // Build a userContact object whether we have a contactId or only an entity
  let userContact: any = null;

  if (contactId) {
    // We have an actual contact in the store (or will fetch it)
    userContact = {
      id: contactId,
      // Prefer data from Redux store if available
      name: contactFromStore?.name || userEntity?.name || '',
      firstName: contactFromStore?.first_name,
      lastName: contactFromStore?.last_name,
      // Use entity identifier as fallback for phone
      phone_number: contactFromStore?.phone_number || (userEntity?.tag === 'twilio' ? userEntity.identifier : undefined),
      // Use memory data or entity for email fallback
      email: contactFromStore?.email || activeConversation.memory?.user_details?.email || (userEntity?.tag === 'email' ? userEntity.identifier : undefined),
      picture: contactFromStore?.picture_url || activeConversation.memory?.user_details?.picture_url,
      company_name: contactFromStore?.company_name,
      tags: contactFromStore?.tags || [],
      favorite: contactFromStore?.favorite || contactFromStore?.tags?.includes('isfavorite') || contactFromStore?.tags?.includes('favorite'),
      last_contacted: contactFromStore?.last_contacted,
      status: contactFromStore?.status,
      role: 'user',
      // Allow us to know if we're showing placeholder data
      isLoading: !contactFromStore && !!contactId
    };
  } else if (userEntity) {
    // No contact yet – derive minimal info from entity & memory
    userContact = {
      id: userEntity.id,
      name: userEntity.name || '',
      firstName: undefined,
      lastName: undefined,
      phone_number: userEntity.tag === 'twilio' ? userEntity.identifier : undefined,
      email: userEntity.tag === 'email' ? userEntity.identifier : undefined,
      picture: activeConversation.memory?.user_details?.picture_url,
      company_name: undefined,
      tags: [],
      favorite: false,
      last_contacted: undefined,
      status: undefined,
      role: 'user',
      isLoading: false
    };
  }

  // Get assistant information
  const assistantContact = assistantEntity ? {
    id: assistantEntity.id,
    name: assistantEntity.name,
    team: '',
    agency: '',
    role: 'assistant'
  } : null;

  // Generate display name based on available data
  const getDisplayName = (contact) => {
    if (contact.firstName && contact.lastName) {
      return `${contact.firstName} ${contact.lastName}`;
    }
    return contact.name || 'Contact';
  };

  // Get contact status - similar to ContactListItem
  const getContactStatus = (contact): string => {
    if (contact.last_contacted) {
      const lastActive = new Date(contact.last_contacted);
      const now = new Date();
      const daysSinceActive = Math.floor((now.getTime() - lastActive.getTime()) / (1000 * 60 * 60 * 24));

      if (daysSinceActive < 7) return 'active';
      if (daysSinceActive < 30) return 'pending';
      return 'inactive';
    }
    return 'new';
  };

  // Get tooltip text for status dot
  const getStatusTooltip = (status: string): string => {
    switch (status) {
      case 'active':
        return 'Active: Contacted within the last 7 days';
      case 'pending':
        return 'Pending: Contacted between 7-30 days ago';
      case 'inactive':
        return 'Inactive: No contact in over 30 days';
      case 'new':
        return 'New: Never contacted';
      default:
        return 'Unknown status';
    }
  };

  // Status dot component
  const StatusDot = ({ status }: { status: string }) => {
    const getStatusColor = () => {
      switch (status) {
        case 'active':
          return '#4caf50'; // Green
        case 'pending':
          return '#ff9800'; // Orange
        case 'inactive':
          return '#f44336'; // Red
        case 'new':
          return '#2196f3'; // Blue
        default:
          return '#9e9e9e'; // Gray
      }
    };

    return (
      <Circle
        sx={{
          fontSize: '8px',
          color: getStatusColor(),
          mr: 0.5,
        }}
      />
    );
  };

  // Handle opening contact dialog
  const handleOpenContactDialog = (event: React.MouseEvent<HTMLDivElement>) => {
    // prevent default or propagation
    event.preventDefault();
    event.stopPropagation();
    console.log('ConversationContacts: handleOpenContactDialog called', { contactId, contactDialogOpen });
    if (contactId) {
      setContactDialogOpen(true);
      console.log('ConversationContacts: setting contactDialogOpen to true');
    } else {
      console.log('ConversationContacts: no contactId available');
    }
  };

  // Handle opening channel/assistant dialog  
  const handleOpenChannelDialog = (event: React.MouseEvent<HTMLDivElement>) => {
    // prevent default or propagation
    event.preventDefault();
    event.stopPropagation();
    if (activeConversation.channel_id) {
      // Close conversation first if the callback exists
      if (onCloseConversation) {
        onCloseConversation();
      }
      setChannelDialogOpen(true);
    }
  };

  // Handle closing contact dialog
  const handleCloseContactDialog = () => {
    setContactDialogOpen(false);
  };

  // Handle closing channel dialog
  const handleCloseChannelDialog = () => {
    setChannelDialogOpen(false);
  };

  return (
    <Box sx={containerStyles}>
      {/* User Contact Card */}
      {!userHidden && userContact && (
        <Tooltip
              open={tooltipOpen}
              onOpen={handleTooltipOpen}
              onClose={handleTooltipClose}
              title={contactId && contactFromStore ? (
                <Box sx={{ p: 1, maxWidth: 300 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {getDisplayName(userContact)}
                  </Typography>
                  {userContact.company_name && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <Business sx={{ fontSize: '14px', mr: 0.5, verticalAlign: 'middle' }} />
                      {userContact.company_name}
                    </Typography>
                  )}
                  {userContact.status && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Status: {userContact.status}
                    </Typography>
                  )}
                  {userContact.last_contacted && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Last contacted: {new Date(userContact.last_contacted).toLocaleDateString()}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                    Click to view full contact details
                  </Typography>
                </Box>
              ) : `${getDisplayName(userContact)} - Click to view details`}
              arrow
              placement="top"
              disableInteractive={false}
            >
              <Box
                sx={cardStyles.minimalContact}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleOpenContactDialog(event);
                }}
                onMouseEnter={handleTooltipOpen}
                onMouseLeave={handleTooltipClose}
              >
                <Avatar sx={{ ...cardStyles.minimalAvatar, '& img': { objectFit: 'cover' } }} src={userContact.picture}>
                  {getDisplayName(userContact).charAt(0) || <Person />}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {getDisplayName(userContact)}
                  </Typography>
                  {(userContact.email || userContact.phone_number) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {userContact.email ? (
                        <Email sx={{ fontSize: 12, color: darkMode ? '#aaa' : '#666' }} />
                      ) : (
                        <Phone sx={{ fontSize: 12, color: darkMode ? '#aaa' : '#666' }} />
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {userContact.email || userContact.phone_number}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Tooltip>
      )}

      {/* Assistant Card - Always minimal */}
      {!assistantHidden && assistantContact && (
        <Box
          sx={cardStyles.minimalContact}
          onClick={(event) => handleOpenChannelDialog(event)}
        >
          <Avatar sx={{ ...cardStyles.minimalAvatar, bgcolor: theme.palette.primary.main, color: 'white' }}>
            {assistantContact.name ? assistantContact.name.charAt(0) : <VerifiedUser />}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {assistantContact.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Oppy Assistant
            </Typography>
          </Box>
        </Box>
      )}

      {/* Contact Dialog */}
      {contactDialogOpen && contactId && (
        <>
                {console.log('ConversationContacts: Rendering ContactDrawer', { contactDialogOpen, contactId })}
      <ContactDrawer
        contactId={contactId}
        open={contactDialogOpen}
        onClose={handleCloseContactDialog}
      />
        </>
      )}

      {/* Channel/Assistant Dialog */}
      {channelDialogOpen && (
        <ChannelEditDialog
          open={channelDialogOpen}
          handleClose={handleCloseChannelDialog}
          handleBack={handleCloseChannelDialog}
          channelId={activeConversation.channel_id}
        />
      )}
    </Box>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(ConversationContacts, (prevProps, nextProps) => {
  return (
    prevProps.conversation_id === nextProps.conversation_id &&
    prevProps.conversation === nextProps.conversation &&
    JSON.stringify(prevProps.hiddenRoles) === JSON.stringify(nextProps.hiddenRoles) &&
    prevProps.onCloseConversation === nextProps.onCloseConversation
  );
});
